/**
 * Adapters for the per-service streams of ADR-0009 (design/07 §9.2) — T14-09: the app calls the
 * same `subscribe(topic, …)` and never knows whether a topic comes from the hub or a service.
 *
 * | topic | stream (gateway) | message |
 * |---|---|---|
 * | `inventory.changed:<variantId>` / `inventory.changed:*` | inventory-service `GET /api/v1/pos/inventory/stream?topics=inventory.changed:<id>,…` (admin: `/api/v1/admin/inventory/stream`) | `payload {variantId, available, quantity, reason, occurredAt}` — POS disables "sell" at once |
 * | `inbox` | notification-service `GET /api/v1/admin/inbox/stream?locale=` | `payload {summary, item}` (the badge) |
 * | `order.tracking:<number>` | order-service `GET /api/v1/store/orders/<number>/events?token=` | `payload` = tracking snapshot (`version`) |
 *
 * Each stream has the hub's reliability rules: reconnect 1 → 30 s with `Last-Event-ID`,
 * `Retry-After`, 3 failures → `fallback` (the client polls), a silent stream (> `stallMs`) is
 * reopened, `resync` → subscribers refetch. These streams cannot change topics in place, so a new
 * topic reopens the connection (batched, resuming from the last id — nothing is lost).
 */
import {
  parseControl,
  retryAfterMs,
  runFetchStream,
  type ChangeMessage,
  type SseEvent,
} from "./sse";
import { INBOX_TOPIC, INVENTORY_PREFIX, TRACKING_PREFIX } from "./topics";
import {
  type ConnectionStatus,
  retryDelay,
  type RetryOptions,
  type Source,
  type SourceFactory,
  type SourceHost,
  type TopicStatus,
} from "./types";

/** What one SSE event of a service stream means. */
export interface StreamAction {
  messages?: ChangeMessage[];
  /** Subscribers of the group refetch (the stream lost track). */
  resync?: boolean;
  /** Close and open again (resuming by id) — e.g. order-service `close {reconnect:true}`. */
  reopen?: boolean;
}

export interface ServiceStreamSpec {
  /** Adapter name (`ChangeMessage.source`). */
  name: string;
  accepts(topic: string): boolean;
  /** Topics sharing one connection (default: all of them). */
  group?(topic: string): string;
  /** URL of the connection for these topics (`null` = nothing to open). */
  url(topics: readonly string[], host: SourceHost): string | null;
  /** Send the bearer token (default true). */
  auth?: boolean;
  /** Most topics per connection (the rest report `fallback`). */
  maxTopics?: number;
  translate(event: SseEvent, topics: ReadonlySet<string>): StreamAction | null;
}

interface Group {
  key: string;
  topics: Set<string>;
  /** Topics of the open connection. */
  wire: Set<string>;
  controller: AbortController | null;
  lastEventId: string | null;
  failures: number;
  everLive: boolean;
  resyncOnOpen: boolean;
  /** 403 / 400: this set of topics is refused until it changes. */
  refused: boolean;
  live: boolean;
  retryTimer: ReturnType<typeof setTimeout> | null;
}

export function createServiceStream(
  spec: ServiceStreamSpec,
  host: SourceHost,
  options: RetryOptions = {},
): Source {
  const minRetryMs = options.minRetryMs ?? 1_000;
  const maxRetryMs = options.maxRetryMs ?? 30_000;
  const fallbackAfter = options.fallbackAfter ?? 3;
  const stallMs = options.stallMs ?? 45_000;
  const batchMs = options.batchMs ?? 50;
  const groups = new Map<string, Group>();
  let paused: "hidden" | "offline" | "pagehide" | null = null;
  let batchTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;

  const keyOf = (topic: string) => spec.group?.(topic) ?? "";

  function groupStatus(g: Group): ConnectionStatus {
    if (g.refused || g.failures >= fallbackAfter) {
      return "fallback";
    }
    if (g.live) {
      return "live";
    }
    return g.everLive || g.failures > 0 ? "reconnecting" : "connecting";
  }

  function capped(g: Group): string[] {
    return [...g.topics].slice(0, spec.maxTopics ?? Infinity);
  }

  function dropConn(g: Group): void {
    g.controller?.abort();
    g.controller = null;
    g.live = false;
  }

  function stopGroup(g: Group): void {
    dropConn(g);
    if (g.retryTimer !== null) {
      clearTimeout(g.retryTimer);
      g.retryTimer = null;
    }
  }

  function schedule(): void {
    if (batchTimer === null && !closed) {
      batchTimer = setTimeout(() => {
        batchTimer = null;
        reconcile();
      }, batchMs);
    }
  }

  function reconcile(): void {
    if (closed || paused !== null) {
      return;
    }
    for (const g of [...groups.values()]) {
      if (g.topics.size === 0) {
        stopGroup(g);
        groups.delete(g.key);
        continue;
      }
      if (g.retryTimer !== null || g.refused) {
        continue;
      }
      const want = capped(g);
      const same =
        g.controller !== null && want.length === g.wire.size && want.every((t) => g.wire.has(t));
      if (!same) {
        dropConn(g);
        void open(g, want);
      }
    }
    host.statusChanged();
  }

  function fail(g: Group, waitAtLeast = 0): void {
    dropConn(g);
    g.failures += 1;
    host.statusChanged();
    g.retryTimer = setTimeout(
      () => {
        g.retryTimer = null;
        reconcile();
      },
      Math.max(waitAtLeast, retryDelay(g.failures, minRetryMs, maxRetryMs)),
    );
  }

  function reopen(g: Group): void {
    dropConn(g);
    host.statusChanged();
    g.retryTimer = setTimeout(() => {
      g.retryTimer = null;
      reconcile();
    }, minRetryMs / 2);
  }

  function handle(g: Group, event: SseEvent): boolean {
    if (event.id) {
      g.lastEventId = event.id;
    }
    const action = spec.translate(event, g.wire);
    for (const message of action?.messages ?? []) {
      host.change({ ...message, source: spec.name });
    }
    if (action?.resync) {
      host.resync([...g.wire], "hub");
    }
    if (action?.reopen) {
      reopen(g);
      return true;
    }
    return false;
  }

  async function open(g: Group, want: string[]): Promise<void> {
    const url = spec.url(want, host);
    if (url === null) {
      return;
    }
    const controller = new AbortController();
    g.controller = controller;
    g.wire = new Set(want);
    const bearer = spec.auth === false ? null : await host.token();
    if (g.controller !== controller) {
      return;
    }
    await runFetchStream({
      fetch: host.fetch,
      url,
      bearer,
      lastEventId: g.lastEventId,
      controller,
      stallMs,
      onRefused: (response) => {
        if (response.status === 401 && spec.auth !== false && host.hasTokenProvider()) {
          host.refuseToken();
        } else if ([400, 401, 403, 404].includes(response.status)) {
          // not allowed / not there — no point retrying until the topics change
          g.refused = true;
          dropConn(g);
          host.statusChanged();
          return;
        }
        fail(g, retryAfterMs(response));
      },
      onOpen: () => {
        g.live = true;
        const resumed = g.everLive;
        g.everLive = true;
        g.failures = 0;
        host.statusChanged();
        if (g.resyncOnOpen || (resumed && !g.lastEventId)) {
          const reason = g.resyncOnOpen ? "resume" : "hub";
          g.resyncOnOpen = false;
          host.resync([...g.wire], reason);
        }
      },
      onEvent: (event) => handle(g, event),
      onFail: () => {
        fail(g);
      },
      onEnd: () => {
        reopen(g);
      },
    });
  }

  function groupFor(topic: string): Group | undefined {
    return groups.get(keyOf(topic));
  }

  return {
    name: spec.name,
    accepts: (topic) => spec.accepts(topic),
    add(topic) {
      const key = keyOf(topic);
      let g = groups.get(key);
      if (!g) {
        g = {
          key,
          topics: new Set(),
          wire: new Set(),
          controller: null,
          lastEventId: null,
          failures: 0,
          everLive: false,
          resyncOnOpen: false,
          refused: false,
          live: false,
          retryTimer: null,
        };
        groups.set(key, g);
      }
      g.topics.add(topic);
      g.refused = false;
      schedule();
    },
    remove(topic) {
      const g = groupFor(topic);
      if (g?.topics.delete(topic)) {
        g.refused = false;
        schedule();
      }
    },
    status() {
      const list = [...groups.values()].filter((g) => g.topics.size > 0);
      if (paused !== null) {
        return paused === "offline" ? "offline" : "paused";
      }
      if (list.length === 0) {
        return "idle";
      }
      const order: ConnectionStatus[] = ["fallback", "reconnecting", "connecting", "live"];
      const states = list.map(groupStatus);
      return order.find((s) => states.includes(s)) ?? "live";
    },
    topicStatus(topic): TopicStatus {
      const g = groupFor(topic);
      if (!g) {
        return "connecting";
      }
      const s = groupStatus(g);
      if (s === "fallback" || !capped(g).includes(topic)) {
        return "fallback";
      }
      return s === "live" && g.wire.has(topic) ? "live" : "connecting";
    },
    pause(reason) {
      paused = reason;
      for (const g of groups.values()) {
        stopGroup(g);
      }
      host.statusChanged();
    },
    resume() {
      if (paused === null) {
        return;
      }
      const wasOffline = paused === "offline";
      paused = null;
      for (const g of groups.values()) {
        g.resyncOnOpen = true;
        if (wasOffline) {
          g.failures = 0;
        }
      }
      reconcile();
    },
    tokenChanged() {
      // these streams check the token when they open; nothing to renew in place
    },
    orderTokenChanged() {
      for (const g of groups.values()) {
        g.refused = false;
        dropConn(g);
      }
      schedule();
    },
    wireTopics: () => [...groups.values()].flatMap((g) => (g.controller ? [...g.wire] : [])),
    close() {
      closed = true;
      if (batchTimer !== null) {
        clearTimeout(batchTimer);
        batchTimer = null;
      }
      for (const g of groups.values()) {
        stopGroup(g);
      }
      groups.clear();
    },
  };
}

/** A custom per-service stream for `createRealtimeClient({ streams: [serviceStream(spec)] })`. */
export function serviceStream(spec: ServiceStreamSpec): SourceFactory {
  return (host, options) => createServiceStream(spec, host, options);
}

// ---- the three streams of ADR-0009 ----------------------------------------------------------------

export interface InventoryStreamOptions {
  /** Gateway path. POS default `/api/v1/pos/inventory/stream`; admin `/api/v1/admin/inventory/stream`. */
  path?: string;
  /** Register of the POS (quantity of its location; also streams what it looked up). */
  registerId?: string | null;
  locationId?: string | null;
  /**
   * Longest URL sent (default 6,000 characters). More variants than fit → the stream asks for
   * `inventory.changed:*` and keeps only the held topics (T14-09 review: 500 ids ≈ 27 KB, past
   * uvicorn's 16 KB request head and the proxy URL limit — the request would fail as a
   * "connection error" and the screen stay on "Updates every 30 s").
   */
  maxUrlLength?: number;
}

/** Availability of variants — `inventory.changed:<variantId>` (T03-17). */
export function inventoryStream(options: InventoryStreamOptions = {}): SourceFactory {
  const path = options.path ?? "/api/v1/pos/inventory/stream";
  return serviceStream({
    name: "inventory",
    accepts: (topic) => topic.startsWith(INVENTORY_PREFIX),
    url(topics) {
      const build = (list: string) => {
        const params = new URLSearchParams();
        params.set("topics", list);
        if (options.registerId) {
          params.set("registerId", options.registerId);
        }
        if (options.locationId) {
          params.set("locationId", options.locationId);
        }
        return `${path}?${params.toString()}`;
      };
      const all = `${INVENTORY_PREFIX}*`;
      const named = build(topics.includes(all) ? all : topics.join(","));
      return named.length <= (options.maxUrlLength ?? 6_000) ? named : build(all);
    },
    translate(event, topics) {
      if (event.event === "resync") {
        return { resync: true };
      }
      if (event.event !== "inventory.changed") {
        return null;
      }
      const data = parseControl(event.data);
      if (typeof data.variantId !== "string") {
        return null;
      }
      const at = typeof data.occurredAt === "string" ? data.occurredAt : new Date().toISOString();
      const base = {
        id: data.variantId,
        entity: "variant",
        action: "availability_changed",
        version: null,
        at,
        payload: data,
      };
      const targets = [`${INVENTORY_PREFIX}${data.variantId}`, `${INVENTORY_PREFIX}*`];
      return {
        messages: targets.filter((t) => topics.has(t)).map((topic) => ({ ...base, topic })),
      };
    },
  });
}

export interface InboxStreamOptions {
  /** Gateway path. Default `/api/v1/admin/inbox/stream`. */
  path?: string;
  /** Language of `item.display` (ja/th/en). */
  locale?: string | (() => string | null) | null;
}

/** The admin to-do badge — topic `inbox` (T12-31). */
export function inboxStream(options: InboxStreamOptions = {}): SourceFactory {
  const path = options.path ?? "/api/v1/admin/inbox/stream";
  return serviceStream({
    name: "inbox",
    accepts: (topic) => topic === INBOX_TOPIC,
    url() {
      const locale = typeof options.locale === "function" ? options.locale() : options.locale;
      return locale ? `${path}?locale=${encodeURIComponent(locale)}` : path;
    },
    translate(event) {
      if (event.event === "resync") {
        return { resync: true, reopen: true };
      }
      if (event.event !== "inbox.changed") {
        return null;
      }
      const data = parseControl(event.data);
      const item = data.item as { id?: unknown; version?: unknown; changedAt?: unknown } | null;
      return {
        messages: [
          {
            topic: INBOX_TOPIC,
            entity: "inbox_item",
            id: typeof item?.id === "string" ? item.id : "summary",
            action: item ? "changed" : "summary",
            version: typeof item?.version === "number" ? item.version : null,
            at: typeof item?.changedAt === "string" ? item.changedAt : new Date().toISOString(),
            payload: data,
          },
        ],
      };
    },
  });
}

export interface TrackingStreamOptions {
  /**
   * URL of an order's stream. Default = gateway `/api/v1/store/orders/<n>/events?token=<t>`
   * (the storefront may pass its BFF route instead).
   */
  url?: (orderNumber: string, token: string | null) => string;
}

const TRACKING_EVENTS: Record<string, string> = {
  snapshot: "snapshot",
  "order.status.changed": "status_changed",
  "payment.status.changed": "payment_status_changed",
  "document.ready": "document_ready",
};

/** Tracking page snapshot — `order.tracking:<number>` (T05-11), one connection per order. */
export function trackingStream(options: TrackingStreamOptions = {}): SourceFactory {
  const url =
    options.url ??
    ((n: string, token: string | null) =>
      `/api/v1/store/orders/${encodeURIComponent(n)}/events${token ? `?token=${encodeURIComponent(token)}` : ""}`);
  return serviceStream({
    name: "tracking",
    accepts: (topic) => topic.startsWith(TRACKING_PREFIX),
    group: (topic) => topic,
    auth: false,
    url(topics, host) {
      const first = topics[0];
      return first ? url(first.slice(TRACKING_PREFIX.length), host.orderToken()) : null;
    },
    translate(event, topics) {
      if (event.event === "resync" || event.event === "close") {
        return { resync: event.event === "resync", reopen: true };
      }
      const action = TRACKING_EVENTS[event.event];
      const topic = [...topics][0];
      if (!action || !topic) {
        return null;
      }
      const data = parseControl(event.data);
      return {
        messages: [
          {
            topic,
            entity: "order",
            id: topic.slice(TRACKING_PREFIX.length),
            action,
            version: typeof data.version === "number" ? data.version : null,
            at: new Date().toISOString(),
            payload: data,
          },
        ],
      };
    },
  });
}
